/**
 * 驾驶行为分析 — 百度地图 (BMap) 版工具函数
 * 与 driveAnalysis.ts 功能一致，使用百度地图 API 替代高德地图 API
 */
import gcoord from 'gcoord';
import { isNil } from 'lodash';
import { ownRound } from '@/utils/math';
import { formatTime } from '@/utils/formatTime';
import { formatGpsSpeed } from '@/utils/gps';
import { displayJtDeviceAlarm } from '@/utils/jtDeviceAlarm';
import { currentLocationRequest } from '@/service/trackPlayback';
import { alarmTypeData } from '@/constants/alarm';
import carIcon from '@/assets/image/carIcon.png';
import onlineCarIcon from '@/assets/image/onlineCarIcon.png';
import { driveVideoErrorMessage } from '@/constants/driveVideo';

// 展示驾驶行为的事件类型（与原函数一致，无地图依赖）
export { renderEventType, renderDvrVideoStatus } from '@/utils/driveAnalysis';

/** 精灵图常量：720px 宽，46px 高，共 20 个图标 */
const SPRITE_URL = '/images/map/pin-sprite.png';
const SPRITE_WIDTH = 720;
const SPRITE_HEIGHT = 46;
const ICON_COUNT = 20;
const ICON_WIDTH = SPRITE_WIDTH / ICON_COUNT; // 36
const ICON_DISPLAY_WIDTH = 18;
const ICON_DISPLAY_HEIGHT = 23;
const RENDER_CHUNK_SIZE = 300;

const POINT_COLLECTION_COLORS: Record<number, string> = {
    0: '#ff4d4f',
    1: '#52c41a',
    3: '#faad14',
    5: '#1677ff'
};

const waitNextFrame = () =>
    new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
    });

const getPointCollectionColor = (iconIndex: number) =>
    POINT_COLLECTION_COLORS[iconIndex] || '#d340c0';

const buildAlarmLabel = (item: any, bmapPoint: any) => {
    const {
        alarmTypeStr,
        vehicleSpeed,
        speed,
        reportedTime,
    } = item;
    const labelString = `<div class="mapAlarmLable">报警类型: ${alarmTypeStr}
        ${vehicleSpeed === null
            ? ''
            : `<br>车身速度: ${ownRound(vehicleSpeed, 1)}`
        }
        ${speed === null ? '' : `<br>gps速度: ${formatGpsSpeed(speed)}`}
        <br/>时间: ${formatTime(reportedTime)}</div>`;

    const label = new window.BMap.Label(labelString, {
        position: bmapPoint,
        offset: new window.BMap.Size(-10, -10)
    });
    label.setStyle({
        border: 'none',
        backgroundColor: 'transparent',
        zIndex: 123
    });
    return label;
};

const appendAlarmLabel = (alarmLabelsRef: any, item: any, label: any) => {
    const { type, alarmType } = item;

    if (type === 'AEB设备') {
        switch (alarmType) {
            case 1:
                alarmLabelsRef.current[1].push(label);
                break;
            case 2:
                alarmLabelsRef.current[2].push(label);
                break;
            case 3:
                alarmLabelsRef.current[3].push(label);
                break;
            case 4:
                alarmLabelsRef.current[4].push(label);
                break;
            case 5:
                alarmLabelsRef.current[5].push(label);
                break;
            case 7:
                alarmLabelsRef.current[8].push(label);
                break;
            case 10:
                alarmLabelsRef.current[9].push(label);
                break;
            default:
                alarmLabelsRef.current[6].push(label);
        }
    }

    if (type === '视频机设备') {
        alarmLabelsRef.current[7].push(label);
    }
};

/**
 * 初始化报警图标工厂（柯里化）
 *
 * 调用时预加载精灵图并创建内部缓存，返回一个对象：
 * - `getIconByIndex(index)`: 根据 0~19 的索引裁剪对应图标，返回 BMap.Icon 实例
 * - `dispose()`: 释放缓存与内部资源，应在离开页面时调用以防止内存泄漏
 *
 * @example
 * ```ts
 * const { getIconByIndex, dispose } = initAlarmIconFactory();
 * const icon = getIconByIndex(3);
 * // 离开页面时
 * dispose();
 * ```
 */
export const initAlarmIconFactory = () => {
    /** 按 index 缓存已创建的 Icon 实例 */
    let iconCache = new Map<number, any>();
    /** 预加载精灵图到内存，后续 BMap.Icon 直接复用浏览器缓存 */
    let spriteImage: HTMLImageElement | null = new Image();
    spriteImage.src = SPRITE_URL;

    /**
     * 根据索引获取对应的报警图标
     * @param index 图标索引 0~19
     * @returns BMap.Icon 实例
     */
    const getIconByIndex = (index: number) => {
        if (index < 0 || index >= ICON_COUNT) {
            console.warn(`[initAlarmIconFactory] index ${index} 超出范围 0~${ICON_COUNT - 1}，将被截断`);
            index = Math.max(0, Math.min(ICON_COUNT - 1, index));
        }

        if (iconCache.has(index)) {
            return iconCache.get(index);
        }

        // 缩放比例：显示尺寸 / 原始单个图标尺寸
        const scaleX = ICON_DISPLAY_WIDTH / ICON_WIDTH;
        const scaleY = ICON_DISPLAY_HEIGHT / SPRITE_HEIGHT;
        const scaledSpriteWidth = SPRITE_WIDTH * scaleX;
        const scaledSpriteHeight = SPRITE_HEIGHT * scaleY;
        const scaledClipPosition = index * ICON_WIDTH * scaleX;

        const icon = new window.BMap.Icon(
            SPRITE_URL,
            new window.BMap.Size(ICON_DISPLAY_WIDTH, ICON_DISPLAY_HEIGHT),
            {
                imageSize: new window.BMap.Size(scaledSpriteWidth, scaledSpriteHeight)
            }
        );
        // 注意：BMap.Icon 构造函数的 imageOffset 选项不生效，需用 setImageOffset 方法
        icon.setImageOffset(new window.BMap.Size(-scaledClipPosition, 0));
        iconCache.set(index, icon);
        return icon;
    };

    /**
     * 销毁工厂资源，清空缓存并释放预加载的图片
     * 应在组件卸载 / 离开页面时调用
     */
    const dispose = () => {
        iconCache.clear();
        iconCache = new Map();
        if (spriteImage) {
            spriteImage.src = '';
            spriteImage = null;
        }
    };

    return { getIconByIndex, dispose };
};

// 驾驶行为的点位信息弹窗展示（百度地图版）
export const showInfoWindowBMap = (point: any, mapRef: any, toCenter?: boolean, address?: any) => {
    const originData = point?.target?.originData || point?.originData || {};
    const { type, vehicleSpeed, gpsSpeed, reportedTime, descText } = originData;
    let content = '';
    if (type === "signInfo") {
        content = `<div><span class='gdInfoLabel'>车身信息</span></div>
      <div><span class='gdInfoLabel'>车身速度: </span> ${ownRound(vehicleSpeed, 1)} </div>
      <div><span class='gdInfoLabel'>时间: </span> ${reportedTime} </div>
      <div><span class='gdInfoLabel'>车身信息: </span> ${descText} </div>`;
    } else if (type === "alarm") {
        const matchAlarmType = alarmTypeData.find(
            (item: any) => item.value === Number(originData.alarmType)
        );
        const eventTypeLabel = matchAlarmType?.label || originData.alarmType;
        content = `<div><span class='gdInfoLabel'>报警信息</span></div>
    <div><span class='gdInfoLabel'>类型: </span> ${eventTypeLabel} </div>
    <div><span class='gdInfoLabel'>车身速度: </span> ${ownRound(vehicleSpeed, 1)} </div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>`;
    } else if (type === "gps" || type === "gps808") {
        content = `<div><span class='gdInfoLabel'>gps信息</span></div>
    <div><span class='gdInfoLabel'>GPS速度: </span>${ownRound(gpsSpeed, 1)}</div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>
    <div><span class='gdInfoLabel'>地点: </span>${address}</div>`;
    } else if (type === "modem") {
        content = `<div><span class='gdInfoLabel'>记录仪信息</span></div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>`;
    } else if (type === "alarm808") {
        content = `<div><span class='gdInfoLabel'>adas报警信息</span></div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>
    <div><span class='gdInfoLabel'>车身速度: </span> ${ownRound(vehicleSpeed, 1)} </div>
    <div><span class='gdInfoLabel'>GPS速度: </span>${ownRound(gpsSpeed, 1)}</div>
    <div style="max-width: 400px"><span class='gdInfoLabel' >信息: </span>${descText}</div>
    `;
    }
    const lnglat = point.lnglat || point.point;
    if (toCenter) {
        mapRef.current.setCenter(lnglat);
    }
    const mapInfoWindow = new window.BMap.InfoWindow(content);
    mapRef.current.openInfoWindow(mapInfoWindow, lnglat);
}

// 驾驶行为的点位信息弹窗展示V2（百度地图版，适配数据迁移后的数据结构）
export const showInfoWindowV2BMap = (point: any, mapRef: any, toCenter?: boolean, address?: any) => {
    const originData = point?.target?.originData || point?.originData || {};
    const { type, vehicleSpeed, speed, reportedTime, descText, alarmTypeStr } = originData;
    let content = '';
    if (alarmTypeStr === "车身信息") {
        content = `<div><span class='gdInfoLabel'>车身信息</span></div>
      <div><span class='gdInfoLabel'>车身速度: </span> ${ownRound(vehicleSpeed, 1)} </div>
      <div><span class='gdInfoLabel'>时间: </span> ${reportedTime} </div>
      <div><span class='gdInfoLabel'>车身信息: </span> ${descText} </div>`;
    }
    else if (alarmTypeStr === "GPS") {
        content = `<div><span class='gdInfoLabel'>gps信息</span></div>
    <div><span class='gdInfoLabel'>GPS速度: </span>${ownRound(speed, 1)}</div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>
    // <div><span class='gdInfoLabel'>地点: </span>${address}</div>`;
    }
    else {
        content = `<div><span class='gdInfoLabel'>报警信息</span></div>
    <div><span class='gdInfoLabel'>时间: </span>${reportedTime}</div>
    <div><span class='gdInfoLabel'>类型: </span> ${alarmTypeStr} </div>
    <div><span class='gdInfoLabel'>车身速度: </span> ${ownRound(vehicleSpeed, 1)} </div>
    <div><span class='gdInfoLabel'>GPS速度: </span>${ownRound(speed, 1)}</div>
    `;
    }
    const lnglat = point.lnglat || point.point;
    if (toCenter) {
        mapRef.current.setCenter(lnglat);
    }
    const mapInfoWindow = new window.BMap.InfoWindow(content);
    mapRef.current.openInfoWindow(mapInfoWindow, lnglat);
}

/**
 * 地图上显示GPS点位（百度地图版）
 * @param data gps数据
 * @param mapRef 地图实例
 * @param playbackUse 轨迹回放页面使用(会有字段使用的特殊处理)
 * @param hiddenItineraryId 是否隐藏行程ID区分
 */
export const renderGpsBMap = (
    data: any,
    mapRef: any,
    playbackUse?: boolean,
    hiddenItineraryId?: boolean
) => {
    const _getAddress = (dataItem: any): Promise<string> => {
        return new Promise((resolve, reject) => {
            const geocoder = new window.BMap.Geocoder();
            const point = new window.BMap.Point(dataItem.lng || dataItem.lat, dataItem.lat || dataItem.lon);
            geocoder.getLocation(point, (result: any) => {
                if (result) {
                    resolve(result.address);
                } else {
                    reject('geocoder failed');
                }
            });
        });
    }

    const points: any = [];
    const pathArr: any = [];
    data.reduce((prev: any, curr: any) => {
        const transformGcoord = gcoord.transform(
            [curr.lat, curr.lon],
            gcoord.WGS84,
            gcoord.BD09
        );
        const bmapPoint = new window.BMap.Point(
            transformGcoord[0],
            transformGcoord[1]
        );
        const currData = playbackUse
            ? { ...curr, type: 'gps', gpsSpeed: curr.speed, transformGcoord }
            : { ...curr, transformGcoord };
        points.push({
            lnglat: bmapPoint,
            style: 0,
            originData: currData
        });
        if (hiddenItineraryId) {
            return curr;
        }
        // 根据行程ID进行区分
        if (prev) {
            if (curr.itineraryId !== prev.itineraryId) {
                if (curr.lat !== prev.lat || curr.lon !== prev.lon) {
                    const prevTransformGcoord = gcoord.transform(
                        [prev.lat, prev.lon],
                        gcoord.WGS84,
                        gcoord.BD09
                    );
                    const prevPoint = new window.BMap.Point(
                        prevTransformGcoord[0],
                        prevTransformGcoord[1]
                    );
                    pathArr.push([prevPoint, bmapPoint]);
                } else {
                    pathArr.push([bmapPoint]);
                }
            } else {
                pathArr[pathArr.length - 1].push(bmapPoint);
            }
        } else {
            pathArr.push([bmapPoint]);
        }
        return curr;
    }, undefined);
    if (hiddenItineraryId) {
        const polyline = new window.BMap.Polyline(
            points.map((item: any) => item.lnglat),
            {
                strokeColor: '#BB2299',
                strokeWeight: 6,
            }
        );
        mapRef.current.addOverlay(polyline);
    } else {
        // 根据gps点位画线
        // console.time('forEach')
        pathArr.forEach((item: any, index: number) => {
            const polyline = new window.BMap.Polyline(item, {
                strokeColor: index % 2 === 0 ? '#BB2299' : '#04f1fc',
                strokeWeight: 6,
            });
            mapRef.current.addOverlay(polyline);
        });
        // console.timeEnd('forEach')
    }

    // 起始位置的marker
    const startPoint = points[0];
    const startIcon = new window.BMap.Icon(
        '/images/map/dir-marker.png',
        new window.BMap.Size(24, 34),
        {
            imageOffset: new window.BMap.Size(-9, -4),
            imageSize: new window.BMap.Size(135, 40)
        }
    );
    const startPointMarker = new window.BMap.Marker(startPoint.lnglat, {
        icon: startIcon,
        // offset: new window.BMap.Size(-12, -34)
    });
    startPointMarker.originData = startPoint.originData;
    mapRef.current.addOverlay(startPointMarker);
    startPointMarker.addEventListener('click', async () => {
        const address = await _getAddress(startPointMarker.originData);
        showInfoWindowBMap({ lnglat: startPoint.lnglat, target: { originData: startPointMarker.originData } }, mapRef, false, address);
    });

    // 终止位置的marker
    const endPoint = points[points.length - 1];
    const endIcon = new window.BMap.Icon(
        '/images/map/dir-marker.png',
        new window.BMap.Size(24, 34),
        {
            imageOffset: new window.BMap.Size(-97, -4),
            imageSize: new window.BMap.Size(135, 40)
        }
    );
    const endPointMarker = new window.BMap.Marker(endPoint.lnglat, {
        icon: endIcon,
        // offset: new window.BMap.Size(-12, -34)
    });
    endPointMarker.originData = startPoint.originData;
    mapRef.current.addOverlay(endPointMarker);
    endPointMarker.addEventListener('click', async () => {
        const address = await _getAddress(endPointMarker.originData);
        showInfoWindowBMap({ lnglat: endPoint.lnglat, target: { originData: endPointMarker.originData } }, mapRef, false, address);
    });

    // 途径gps点 — 使用普通Marker替代高德MassMarks
    const middlePoints = points.slice(1, points.length - 1);
    const massIcon = new window.BMap.Icon(
        '/images/map/mass0.png',
        new window.BMap.Size(12, 12)
    );
    middlePoints.forEach((item: any) => {
        const marker = new window.BMap.Marker(item.lnglat, {
            icon: massIcon,
            // offset: new window.BMap.Size(-8, -8)
        });
        marker.originData = item.originData;
        mapRef.current.addOverlay(marker);
        marker.addEventListener('click', async () => {
            debugger;
            const address = await _getAddress(marker.originData);
            showInfoWindowBMap({ lnglat: item.lnglat, target: { originData: marker.originData } }, mapRef, false, address);
        });
    });
};

/**
 * 地图上显示GPS点位V2（百度地图版）—— 使用 PointCollection 海量点替代逐个 Marker
 * 功能与 renderGpsBMap 一致，中间 GPS 点改用 BMap.PointCollection（Canvas 渲染）以提升性能
 * @param data gps数据
 * @param mapRef 地图实例
 * @param playbackUse 轨迹回放页面使用(会有字段使用的特殊处理)
 * @param hiddenItineraryId 是否隐藏行程ID区分
 */
export const renderGpsBMapV2 = async (
    data: any,
    mapRef: any,
    playbackUse?: boolean,
    hiddenItineraryId?: boolean
) => {
    if (!Array.isArray(data) || !data.length) {
        return;
    }

    const _getAddress = (dataItem: any): Promise<string> => {
        return new Promise((resolve, reject) => {
            const geocoder = new window.BMap.Geocoder();
            const point = new window.BMap.Point(dataItem.lng || dataItem.lat, dataItem.lat || dataItem.lon);
            geocoder.getLocation(point, (result: any) => {
                if (result) {
                    resolve(result.address);
                } else {
                    reject('geocoder failed');
                }
            });
        });
    }

    const points: any = [];
    const pathArr: any = [];
    let prev: any = undefined;
    for (let i = 0; i < data.length; i += 1) {
        const curr = data[i];
        const transformGcoord = gcoord.transform(
            [curr.lat, curr.lon],
            gcoord.WGS84,
            gcoord.BD09
        );
        const bmapPoint = new window.BMap.Point(
            transformGcoord[0],
            transformGcoord[1]
        );
        const currData = playbackUse
            ? { ...curr, type: 'gps', gpsSpeed: curr.speed, transformGcoord }
            : { ...curr, transformGcoord };
        points.push({
            lnglat: bmapPoint,
            style: 0,
            originData: currData
        });
        if (hiddenItineraryId) {
            prev = curr;
            if (i > 0 && i % RENDER_CHUNK_SIZE === 0) {
                await waitNextFrame();
            }
            continue;
        }
        // 根据行程ID进行区分
        if (prev) {
            if (curr.itineraryId !== prev.itineraryId) {
                if (curr.lat !== prev.lat || curr.lon !== prev.lon) {
                    const prevTransformGcoord = gcoord.transform(
                        [prev.lat, prev.lon],
                        gcoord.WGS84,
                        gcoord.BD09
                    );
                    const prevPoint = new window.BMap.Point(
                        prevTransformGcoord[0],
                        prevTransformGcoord[1]
                    );
                    pathArr.push([prevPoint, bmapPoint]);
                } else {
                    pathArr.push([bmapPoint]);
                }
            } else {
                pathArr[pathArr.length - 1].push(bmapPoint);
            }
        } else {
            pathArr.push([bmapPoint]);
        }
        prev = curr;

        if (i > 0 && i % RENDER_CHUNK_SIZE === 0) {
            await waitNextFrame();
        }
    }

    if (hiddenItineraryId) {
        const polyline = new window.BMap.Polyline(
            points.map((item: any) => item.lnglat),
            {
                strokeColor: '#BB2299',
                strokeWeight: 6,
            }
        );
        mapRef.current.addOverlay(polyline);
    } else {
        // 根据gps点位画线
        for (let i = 0; i < pathArr.length; i += 1) {
            const item = pathArr[i];
            const polyline = new window.BMap.Polyline(item, {
                strokeColor: i % 2 === 0 ? '#BB2299' : '#04f1fc',
                strokeWeight: 6,
            });
            mapRef.current.addOverlay(polyline);
            if (i > 0 && i % RENDER_CHUNK_SIZE === 0) {
                await waitNextFrame();
            }
        }
    }

    // 起始位置的marker
    const startPoint = points[0];
    const startIcon = new window.BMap.Icon(
        '/images/map/dir-marker.png',
        new window.BMap.Size(24, 34),
        {
            imageOffset: new window.BMap.Size(-9, -4),
            imageSize: new window.BMap.Size(135, 40)
        }
    );
    const startPointMarker = new window.BMap.Marker(startPoint.lnglat, {
        icon: startIcon,
    });
    startPointMarker.originData = startPoint.originData;
    mapRef.current.addOverlay(startPointMarker);
    startPointMarker.addEventListener('click', async () => {
        const address = await _getAddress(startPointMarker.originData);
        showInfoWindowBMap({ lnglat: startPoint.lnglat, target: { originData: startPointMarker.originData } }, mapRef, false, address);
    });

    // 终止位置的marker
    const endPoint = points[points.length - 1];
    const endIcon = new window.BMap.Icon(
        '/images/map/dir-marker.png',
        new window.BMap.Size(24, 34),
        {
            imageOffset: new window.BMap.Size(-97, -4),
            imageSize: new window.BMap.Size(135, 40)
        }
    );
    const endPointMarker = new window.BMap.Marker(endPoint.lnglat, {
        icon: endIcon,
    });
    endPointMarker.originData = startPoint.originData;
    mapRef.current.addOverlay(endPointMarker);
    endPointMarker.addEventListener('click', async () => {
        const address = await _getAddress(endPointMarker.originData);
        showInfoWindowBMap({ lnglat: endPoint.lnglat, target: { originData: endPointMarker.originData } }, mapRef, false, address);
    });

    // 途径gps点 — 使用 PointCollection 海量点渲染（Canvas，替代逐个 Marker）
    const middlePoints = points.slice(1, points.length - 1);
    if (middlePoints.length) {
        const bmapPoints = middlePoints.map((item: any) => {
            const pt = item.lnglat;
            (pt as any).originData = item.originData;
            return pt;
        });
        const pointCollection = new window.BMap.PointCollection(bmapPoints, {
            shape: (window as any).BMAP_POINT_SHAPE_CIRCLE,
            size: (window as any).BMAP_POINT_SIZE_SMALL,
            color: '#d340c0',
        });
        pointCollection.addEventListener('click', async (e: any) => {
            const clickedPoint = e.point;
            if (clickedPoint?.originData) {
                const address = await _getAddress(clickedPoint.originData);
                showInfoWindowBMap(
                    { lnglat: clickedPoint, target: { originData: clickedPoint.originData } },
                    mapRef, false, address
                );
            }
        });
        mapRef.current.addOverlay(pointCollection);
    }
};

/**
 * 地图上显示报警信息或车身信息或记录仪信息的marker（百度地图版，异步分片）
 * 与 renderMarkerBMap 保持视觉效果一致，避免大数据量时长时间阻塞主线程
 */
export const renderMarkerBMapAsync = async (
    data: any,
    getIconByIndex: (index: number) => any,
    iconIndex: number,
    mapRef: any,
    alarmLabelsRef?: any,
    hideMark?: boolean
) => {
    if (!Array.isArray(data) || !data.length) {
        return;
    }

    const bmapPoints: any[] = [];

    for (let i = 0; i < data.length; i += 1) {
        const item = data[i];
        const transformGcoord = gcoord.transform(
            [item.lat, item.lon],
            gcoord.WGS84,
            gcoord.BD09
        );
        const bmapPoint = new window.BMap.Point(
            transformGcoord[0],
            transformGcoord[1]
        );

        (bmapPoint as any).originData = item;
        bmapPoints.push(bmapPoint);

        if (alarmLabelsRef) {
            const label = buildAlarmLabel(item, bmapPoint);
            appendAlarmLabel(alarmLabelsRef, item, label);
        }

        if (i > 0 && i % RENDER_CHUNK_SIZE === 0) {
            await waitNextFrame();
        }
    }

    const pointCollection = new window.BMap.PointCollection(bmapPoints, {
        shape: (window as any).BMAP_POINT_SHAPE_CIRCLE,
        size: (window as any).BMAP_POINT_SIZE_SMALL,
        color: getPointCollectionColor(iconIndex),
    });

    if (!hideMark) {
        pointCollection.addEventListener('click', (e: any) => {
            const clickedPoint = e.point;
            if (!clickedPoint?.originData) {
                return;
            }
            showInfoWindowV2BMap(
                { lnglat: clickedPoint, target: { originData: clickedPoint.originData } },
                mapRef
            );
        });
    }

    mapRef.current.addOverlay(pointCollection);

    void getIconByIndex;
};

/**
 * 地图上显示报警信息或车身信息或记录仪信息的marker（百度地图版）
 * @param data 数据
 * @param getIconByIndex 通过 initAlarmIconFactory 获得的图标获取函数
 * @param iconIndex 图标在精灵图中的索引（0~19）
 * @param mapRef 地图实例
 * @param alarmLabelsRef 存储报警label
 * @param hideMark 是否隐藏点击事件
 */
export const renderMarkerBMap = (
    data: any,
    getIconByIndex: (index: number) => any,
    iconIndex: number,
    mapRef: any,
    alarmLabelsRef?: any,
    hideMark?: boolean
) => {
    data.forEach((item: any) => {
        const transformGcoord = gcoord.transform(
            [item.lat, item.lon],
            gcoord.WGS84,
            gcoord.BD09
        );
        const bmapPoint = new window.BMap.Point(
            transformGcoord[0],
            transformGcoord[1]
        );

        // 使用柯里化工厂函数获取精灵图标
        const alarmIcon = getIconByIndex(iconIndex);

        const alarmMarker = new window.BMap.Marker(bmapPoint, {
            icon: alarmIcon
        });
        alarmMarker.originData = item;

        if (!hideMark) {
            alarmMarker.addEventListener('click', () => {
                showInfoWindowV2BMap(
                    { lnglat: bmapPoint, target: { originData: item } },
                    mapRef
                );
            });
        }

        mapRef.current.addOverlay(alarmMarker);

        if (alarmLabelsRef) {
            // 存储报警信息文本标注，勾选信息时显示
            const label = buildAlarmLabel(item, bmapPoint);
            appendAlarmLabel(alarmLabelsRef, item, label);
        }
    });
};

// 地图上设置车辆的位置(在地图中心显示)（百度地图版）
export const setCarLocationBMap = async (
    id: string,
    mapRef: any,
    isOnline?: boolean
) => {
    mapRef.current.clearOverlays();
    const res = await currentLocationRequest(id);
    const locationData = res?.data?.data || {};
    if (locationData.lon && locationData.lat) {
        const transformGcoord = gcoord.transform(
            [locationData.lat, locationData.lon],
            gcoord.WGS84,
            gcoord.BD09
        );
        const bmapPoint = new window.BMap.Point(
            transformGcoord[0],
            transformGcoord[1]
        );
        const icon = new window.BMap.Icon(
            isOnline ? onlineCarIcon : carIcon,
            new window.BMap.Size(32, 32)
        );
        const marker = new window.BMap.Marker(bmapPoint, {
            icon,
            offset: new window.BMap.Size(-10, -10)
        });
        mapRef.current.setCenter(bmapPoint);
        mapRef.current.addOverlay(marker);
    }
};
